import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatError, photoUrl as resolvePhotoUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { JHARKHAND_DISTRICTS, RELIGIONS, EDUCATION, blocksFor, JHARKHAND_CASTES, OCCUPATIONS } from "@/data/jharkhand";
import { Sparkles, Camera, X, ChevronDown, Check, Search, FileText, Upload, Loader2, ChevronLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const STEPS = ["Basics & Photo", "Location & Roots", "Profile Details"];

const HEIGHT_OPTIONS = Array.from({ length: 77 }, (_, i) => {
  const cm = 137 + i;
  const totalInches = cm / 2.54;
  const feet = Math.floor(totalInches / 12);
  const inches = Math.round(totalInches % 12);
  return { cm, label: `${feet}ft ${inches}in (${cm} cm)` };
});

const isBrahmin = (caste = "") => {
  const c = (caste || "").toLowerCase();
  return c.includes("brahmin") || c.includes("brahman");
};

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  
  // Registration choice: null (choice screen), "manual", or "autofill"
  const [stepChoice, setStepChoice] = useState(null);
  const [selectedOption, setSelectedOption] = useState("manual"); // "manual" or "autofill"
  
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    phone: "",
    dob: "",
    photos: [],
    district: "Ranchi",
    block: "",
    village: "",
    religion: "Hindu",
    caste: "",
    subcaste: "",
    gotra: "",
    mool: "",
    native_place: "",
    education: "B.Tech / B.E",
    occupation: "",
    family_type: "Nuclear",
    profile_managed_by: "Self",
    height_cm: "",
    city: "",
    income: "No Income",
  });
  const [photoUrl, setPhotoUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [photoRequired, setPhotoRequired] = useState(true);
  
  // Document Auto-Fill states
  const [extracting, setExtracting] = useState(false);
  const [extractedCount, setExtractedCount] = useState(0);
  const [fileName, setFileName] = useState("");

  const handleExtractBiodata = async (file) => {
    if (!file) return;
    setExtracting(true);
    setFileName(file.name);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/profile/extract-biodata", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const extData = res.data.extracted || {};
      const keysExtracted = Object.keys(extData).filter((k) => extData[k]);
      
      if (keysExtracted.length > 0) {
        setData((prev) => ({
          ...prev,
          ...extData,
        }));
        setExtractedCount(keysExtracted.length);
        toast.success(`✨ Auto-filled ${keysExtracted.length} fields from ${file.name}! Fill any missing details below.`);
      } else {
        toast.info("Could not detect structured text automatically. You can fill details manually below.");
      }
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setExtracting(false);
    }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setData((d) => ({ ...d, photos: [...(d.photos || []), data.url] }));
      toast.success("Photo uploaded successfully!");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if (user) {
      setData((d) => ({
        ...d,
        ...Object.fromEntries(Object.entries(user).filter(([k]) => k in d))
      }));
    }
  }, [user]);

  useEffect(() => {
    api.get("/settings/public")
      .then(({ data }) => {
        if (data.require_photo_onboarding === false) {
          setPhotoRequired(false);
        }
      })
      .catch(() => {});
  }, []);

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const addPhoto = () => {
    if (!photoUrl) return;
    update("photos", [...(data.photos || []), photoUrl]);
    setPhotoUrl("");
  };
  
  const removePhoto = (i) => update("photos", data.photos.filter((_, x) => x !== i));

  const samplePhotos = (gender) => gender === "male"
    ? ["https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800",
       "https://images.unsplash.com/photo-1492562080023-ab3db95bfbce?w=800"]
    : ["https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=800",
       "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=800"];

  const validateAge = (dob) => {
    if (!dob) return false;
    const dobDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - dobDate.getFullYear();
    const m = today.getMonth() - dobDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dobDate.getDate())) {
      age--;
    }
    const gender = (user?.gender || "").toLowerCase();
    if (gender.startsWith("m")) {
      return age >= 21;
    } else {
      return age >= 18;
    }
  };

  const submit = async () => {
    if (!user?.phone) {
      if (!data.phone) return toast.error("Please enter your mobile number");
      const cleanPh = data.phone.trim().replace(/\s+/g, "").replace("+91", "");
      if (!/^\d{10}$/.test(cleanPh)) return toast.error("Please enter a valid 10-digit mobile number");
    }
    if (!data.dob) return toast.error("Please enter your date of birth");
    if (!validateAge(data.dob)) return toast.error("Under Indian Matrimonial Law, the minimum age for a groom (male) is 21 years / bride (female) is 18 years.");
    if (photoRequired && (!data.photos || data.photos.length === 0)) return toast.error("Please upload at least one profile photo");
    if (!data.district) return toast.error("Please select a district");
    if (!data.block) return toast.error("Please select a block");
    if (!data.village.trim()) return toast.error("Please enter your village/locality");
    if (!data.religion) return toast.error("Please select a religion");
    if (!data.education) return toast.error("Please select your highest education");
    if (!data.occupation.trim()) return toast.error("Please enter your occupation/job type");

    setSaving(true);
    try {
      await api.put("/profile", data);
      await refresh();
      toast.success("Profile saved! Welcome to Hamarsathi.");
      navigate("/app");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  const next = () => {
    if (step === 0) {
      if (!user?.phone) {
        if (!data.phone) return toast.error("Please enter your mobile number");
        const cleanPh = data.phone.trim().replace(/\s+/g, "").replace("+91", "");
        if (!/^\d{10}$/.test(cleanPh)) return toast.error("Please enter a valid 10-digit mobile number");
      }
      if (!data.dob) return toast.error("Please enter your date of birth");
      if (!validateAge(data.dob)) return toast.error("Under Indian Matrimonial Law, the minimum age for a groom (male) is 21 years / bride (female) is 18 years.");
      if (photoRequired && (!data.photos || data.photos.length === 0)) return toast.error("Please upload at least one profile photo");
    } else if (step === 1) {
      if (!data.district) return toast.error("Please select a district");
      if (!data.block) return toast.error("Please select a block");
      if (!data.village.trim()) return toast.error("Please enter your village/locality");
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  return (
    <div className="min-h-screen bg-background py-8 sm:py-12">
      <div className="container-px max-w-xl mx-auto">

        {/* ── 1. CHOICE SCREEN (EXACT JEEVANSATHI STYLE) ── */}
        {stepChoice === null && (
          <div className="bg-white rounded-3xl p-6 sm:p-10 shadow-xl border border-border/80 text-center animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Sparkles className="w-6 h-6 text-primary" />
              <h2 className="font-heading text-2xl sm:text-3xl text-primary font-extrabold">
                Welcome to <span className="text-primary">Hamarsathi</span>
              </h2>
            </div>
            <p className="text-xs text-muted-foreground mb-8">Choose how you would like to complete your profile</p>

            <div className="space-y-4">
              {/* Option 1: Enter details manually */}
              <div
                onClick={() => setSelectedOption("manual")}
                className={`p-5 rounded-2xl border-2 transition-all cursor-pointer flex items-center justify-between gap-4 text-left ${
                  selectedOption === "manual"
                    ? "border-primary bg-primary/5 shadow-sm"
                    : "border-gray-200 hover:border-gray-300 bg-white"
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center text-amber-700 shrink-0 text-xl font-bold">
                    👇
                  </div>
                  <div>
                    <h4 className="font-bold text-sm sm:text-base text-foreground">Enter details manually</h4>
                    <p className="text-xs text-muted-foreground mt-0.5">Type details one-by-one</p>
                  </div>
                </div>
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 ${
                  selectedOption === "manual" ? "border-primary bg-primary text-white" : "border-gray-300"
                }`}>
                  {selectedOption === "manual" && <div className="w-2.5 h-2.5 rounded-full bg-white" />}
                </div>
              </div>

              {/* OR Divider */}
              <div className="relative my-4 text-center">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200"></div></div>
                <span className="relative bg-white px-4 text-xs font-bold text-muted-foreground tracking-widest uppercase">OR</span>
              </div>

              {/* Option 2: Upload Biodata (AI Autofill) */}
              <div
                onClick={() => setSelectedOption("autofill")}
                className={`p-5 rounded-2xl border-2 transition-all cursor-pointer flex items-center justify-between gap-4 text-left ${
                  selectedOption === "autofill"
                    ? "border-primary bg-primary/5 shadow-sm"
                    : "border-gray-200 hover:border-gray-300 bg-white"
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-rose-100 flex items-center justify-center text-rose-600 shrink-0 text-xl font-bold">
                    📄
                  </div>
                  <div>
                    <h4 className="font-bold text-sm sm:text-base text-foreground">Upload Biodata (AI Autofill)</h4>
                    <p className="text-xs text-muted-foreground mt-0.5">Upload an existing biodata (PDF or Scanned Image)</p>
                  </div>
                </div>
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 ${
                  selectedOption === "autofill" ? "border-primary bg-primary text-white" : "border-gray-300"
                }`}>
                  {selectedOption === "autofill" && <div className="w-2.5 h-2.5 rounded-full bg-white" />}
                </div>
              </div>
            </div>

            {/* Proceed Button */}
            <Button
              type="button"
              onClick={() => setStepChoice(selectedOption)}
              className="w-full mt-8 py-3.5 bg-primary hover:bg-primary/90 text-white font-bold rounded-full text-base shadow-lg transition flex items-center justify-center gap-2"
            >
              <span>Proceed</span>
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        )}

        {/* ── 2. AUTOFILL SCREEN (WITH BIODATA, PHOTO & MISSING FIELDS COMPLETION) ── */}
        {stepChoice === "autofill" && (
          <div className="animate-in fade-in duration-200 space-y-6">
            <button
              type="button"
              onClick={() => setStepChoice(null)}
              className="flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-primary mb-2 transition"
            >
              <ChevronLeft className="w-4 h-4" /> Change Option
            </button>

            <div className="card-soft p-6 sm:p-8 space-y-8 bg-white border border-border/80 shadow-xl rounded-3xl">
              <div>
                <div className="flex items-center gap-2 text-primary font-bold text-xl mb-1">
                  <FileText className="w-5 h-5 text-rose-600" />
                  <h3>Step 1: Upload Biodata Document</h3>
                </div>
                <p className="text-xs text-muted-foreground">
                  Upload your Biodata in PDF, Word (DOCX), Text, or Scanned Image format. Details will be extracted automatically!
                </p>
              </div>

              {/* Upload Dropzone */}
              <label className="border-2 border-dashed border-primary/30 hover:border-primary/60 rounded-2xl p-6 sm:p-8 flex flex-col items-center justify-center cursor-pointer bg-primary/5 hover:bg-primary/10 transition text-center group">
                <Upload className="w-10 h-10 text-primary/70 group-hover:scale-110 transition mb-3" />
                <span className="text-sm font-semibold text-primary">
                  {fileName ? `File Selected: ${fileName}` : "Click to Upload Biodata File"}
                </span>
                <span className="text-xs text-muted-foreground mt-1">Supports PDF, DOCX, JPG, PNG, TXT (Max 10 MB)</span>
                <input
                  type="file"
                  accept=".pdf,.docx,.doc,.txt,.jpg,.jpeg,.png,.webp"
                  className="hidden"
                  onChange={(e) => handleExtractBiodata(e.target.files?.[0])}
                  disabled={extracting}
                />
              </label>

              {extracting && (
                <div className="flex items-center justify-center gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl text-amber-900 text-xs font-semibold">
                  <Loader2 className="w-4 h-4 animate-spin text-amber-600" />
                  <span>Extracting information from biodata document...</span>
                </div>
              )}

              {/* Extracted Fields Header Banner */}
              {extractedCount > 0 && (
                <div className="p-3.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-between text-xs font-bold text-emerald-900">
                  <span className="flex items-center gap-1.5">
                    <Check className="w-4 h-4 text-emerald-600" /> Auto-filled {extractedCount} details from biodata!
                  </span>
                  <span className="text-[11px] text-emerald-800 font-normal">Check &amp; complete missing details below</span>
                </div>
              )}

              {/* Step 2: Photo Upload */}
              <div className="pt-4 border-t border-border space-y-4">
                <div className="flex items-center gap-2 text-primary font-bold text-xl">
                  <Camera className="w-5 h-5 text-primary" />
                  <h3>Step 2: Upload Profile Photo</h3>
                </div>
                <p className="text-xs text-muted-foreground">Upload at least one clear profile photo (JPG/PNG/WEBP, max 8 MB).</p>
                
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <div className="flex-1">
                    <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => uploadFile(e.target.files?.[0])} className="text-sm w-full" />
                    {uploading && <span className="text-xs text-muted-foreground">Uploading...</span>}
                  </div>
                  <div className="flex gap-2 items-center">
                    <span className="text-xs text-muted-foreground shrink-0">or URL:</span>
                    <Input value={photoUrl} onChange={(e) => setPhotoUrl(e.target.value)} placeholder="https://..." className="min-w-[150px] h-9 text-xs" />
                    <Button type="button" onClick={addPhoto} size="sm" className="rounded-full shrink-0"><Camera className="w-4 h-4 mr-1" />Add</Button>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 text-xs items-center">
                  <span className="text-muted-foreground">Quick sample photos:</span>
                  {samplePhotos(user?.gender).map((u) => (
                    <button key={u} type="button" className="text-secondary font-semibold hover:underline" onClick={() => update("photos", [...(data.photos || []), u])}>Use Sample</button>
                  ))}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-2">
                  {(data.photos || []).map((u, i) => (
                    <div key={i} className="relative aspect-[3/4] rounded-xl overflow-hidden border border-border shadow-sm">
                      <img src={resolvePhotoUrl(u)} alt="Profile photo" className="w-full h-full object-cover" />
                      <button onClick={() => removePhoto(i)} className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1"><X className="w-3 h-3"/></button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Step 3: Complete / Edit Profile Fields (Auto-filled + Fill Missing) */}
              <div className="pt-6 border-t border-border space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-primary font-bold text-xl">
                    <Sparkles className="w-5 h-5 text-amber-500" />
                    <h3>Step 3: Review &amp; Fill Missing Fields</h3>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Fields detected from biodata are filled automatically. Please complete any remaining empty fields.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                  {!user?.phone && (
                    <div className="sm:col-span-2">
                      <Field label="Mobile Number (10 digits) *">
                        <Input
                          type="tel"
                          value={data.phone || ""}
                          onChange={(e) => update("phone", e.target.value)}
                          placeholder="e.g. 9876543210"
                          className={!data.phone ? "border-amber-400 bg-amber-50/50" : "bg-white"}
                        />
                      </Field>
                    </div>
                  )}

                  <div>
                    <Field label="Date of birth *">
                      <Input type="date" value={data.dob} onChange={(e) => update("dob", e.target.value)} className={!data.dob ? "border-amber-400 bg-amber-50/50" : "bg-white"} />
                    </Field>
                  </div>

                  <div>
                    <Field label="Height">
                      <Select value={data.height_cm ? String(data.height_cm) : ""} onValueChange={(v) => update("height_cm", Number(v))}>
                        <SelectTrigger className={!data.height_cm ? "border-amber-400 bg-amber-50/50" : "bg-white"}><SelectValue placeholder="Select height" /></SelectTrigger>
                        <SelectContent>
                          {HEIGHT_OPTIONS.map((h) => <SelectItem key={h.cm} value={String(h.cm)}>{h.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>

                  <div>
                    <Field label="Profile Managed By">
                      <Sel value={data.profile_managed_by || "Self"} onChange={(v) => update("profile_managed_by", v)} items={["Self", "Parent", "Sibling", "Relative", "Friend"]} />
                    </Field>
                  </div>

                  <div>
                    <Field label="District (Jharkhand) *">
                      <Sel value={data.district} onChange={(v) => setData((d) => ({ ...d, district: v, block: "" }))} items={JHARKHAND_DISTRICTS} />
                    </Field>
                  </div>

                  <div>
                    <Field label="Block / Tehsil *">
                      <Select value={data.block || ""} onValueChange={(v) => update("block", v)}>
                        <SelectTrigger className={!data.block ? "border-amber-400 bg-amber-50/50" : "bg-white"}><SelectValue placeholder="Select block" /></SelectTrigger>
                        <SelectContent>
                          {blocksFor(data.district).map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>

                  <div>
                    <Field label="Village / Locality *">
                      <Input value={data.village || ""} onChange={(e) => update("village", e.target.value)} placeholder="Enter village name" className={!data.village ? "border-amber-400 bg-amber-50/50" : "bg-white"} />
                    </Field>
                  </div>

                  <div>
                    <Field label="Religion *">
                      <Sel value={data.religion} onChange={(v) => update("religion", v)} items={RELIGIONS} />
                    </Field>
                  </div>

                  <div>
                    <Field label="Caste">
                      <SearchableSel
                        value={data.caste}
                        onChange={(v) => update("caste", v)}
                        items={JHARKHAND_CASTES}
                        placeholder="Select caste"
                      />
                    </Field>
                  </div>

                  {/* 🌸 BRAHMIN TRADITIONAL DETAILS SECTION ── */}
                  {isBrahmin(data.caste) && (
                    <div className="p-4 rounded-2xl bg-amber-500/10 border border-amber-500/30 space-y-3 col-span-full animate-in fade-in duration-200">
                      <div className="flex items-center justify-between text-amber-900 font-bold text-xs">
                        <span className="flex items-center gap-1.5">
                          🌸 <span>Brahmin Traditional Details</span>
                        </span>
                        <span className="text-[10px] bg-amber-200/80 text-amber-900 font-semibold px-2 py-0.5 rounded-full">Gotra &amp; Mool / Dih</span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                        <div>
                          <Field label="Gotra">
                            <Input value={data.gotra || ""} onChange={(e) => update("gotra", e.target.value)} placeholder="e.g. Shandilya, Kashyap, Bharadwaj" className="bg-white text-xs h-9" />
                          </Field>
                        </div>
                        <div>
                          <Field label="Sub-Caste">
                            <Input value={data.subcaste || ""} onChange={(e) => update("subcaste", e.target.value)} placeholder="e.g. Maithil Brahmin, Kanyakubja" className="bg-white text-xs h-9" />
                          </Field>
                        </div>
                        <div>
                          <Field label="Mool / Dih (Origin Root)">
                            <Input value={data.mool || ""} onChange={(e) => update("mool", e.target.value)} placeholder="e.g. Rampuriye Rohar" className="bg-white text-xs h-9" />
                          </Field>
                        </div>
                        <div>
                          <Field label="Native Place / Village">
                            <Input value={data.native_place || ""} onChange={(e) => update("native_place", e.target.value)} placeholder="e.g. Andouli, Darbhanga" className="bg-white text-xs h-9" />
                          </Field>
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <Field label="Highest Education *">
                      <SearchableSel
                        value={data.education}
                        onChange={(v) => update("education", v)}
                        items={EDUCATION}
                        placeholder="Select education"
                      />
                    </Field>
                  </div>

                  <div>
                    <Field label="Occupation / Job *">
                      <SearchableSel
                        value={data.occupation}
                        onChange={(v) => update("occupation", v)}
                        items={OCCUPATIONS}
                        placeholder="Select occupation"
                      />
                    </Field>
                  </div>

                  <div>
                    <Field label="Annual Income">
                      <Sel value={data.income || "No Income"} onChange={(v) => update("income", v)} items={["No Income", "Below 2 Lakhs", "2 - 5 Lakhs", "5 - 10 Lakhs", "10 - 20 Lakhs", "20+ Lakhs"]} />
                    </Field>
                  </div>

                  <div>
                    <Field label="Family Type">
                      <Sel value={data.family_type} onChange={(v) => update("family_type", v)} items={["Nuclear", "Joint"]} />
                    </Field>
                  </div>
                </div>
              </div>

              {/* Submit Button in Auto Mode */}
              <div className="pt-4 border-t border-border">
                <Button
                  type="button"
                  onClick={submit}
                  disabled={saving}
                  className="w-full py-3.5 bg-primary hover:bg-primary/90 text-white font-bold rounded-full shadow-lg text-base flex items-center justify-center gap-2"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 text-amber-300" />}
                  <span>Complete &amp; Save Profile</span>
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ── 3. MANUAL STEP-BY-STEP SCREEN ── */}
        {stepChoice === "manual" && (
          <div className="animate-in fade-in duration-200">
            <button
              type="button"
              onClick={() => setStepChoice(null)}
              className="flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-primary mb-4 transition"
            >
              <ChevronLeft className="w-4 h-4" /> Change Option
            </button>

            <div className="mb-2 flex items-center justify-between">
              <span className="label-cap">Step {step + 1} of {STEPS.length}</span>
              <span className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">{STEPS[step]}</span>
            </div>
            <Progress value={((step + 1) / STEPS.length) * 100} className="h-1 mb-6" />

            <div className="card-soft p-6 sm:p-8 bg-white border border-border/80 shadow-xl rounded-3xl">
              <h2 className="font-heading text-3xl text-primary mb-6">{STEPS[step]}</h2>

              {step === 0 && (
                <div className="space-y-6">
                  {!user?.phone && (
                    <Field label="Mobile Number (10 digits) *">
                      <Input
                        type="tel"
                        value={data.phone || ""}
                        onChange={(e) => update("phone", e.target.value)}
                        placeholder="e.g. 9876543210"
                        className={!data.phone ? "border-amber-400 bg-amber-50/50" : "bg-white"}
                      />
                    </Field>
                  )}

                  <Field label="Date of birth">
                    <Input type="date" value={data.dob} onChange={(e) => update("dob", e.target.value)} data-testid="ob-dob" />
                  </Field>

                  <Field label="Profile Managed By">
                    <Sel value={data.profile_managed_by || "Self"} onChange={(v) => update("profile_managed_by", v)} items={["Self", "Parent", "Sibling", "Relative", "Friend"]} testid="ob-managedby" />
                  </Field>

                  <Field label="Height">
                    <Select value={data.height_cm ? String(data.height_cm) : ""} onValueChange={(v) => update("height_cm", Number(v))}>
                      <SelectTrigger data-testid="ob-height"><SelectValue placeholder="Select height" /></SelectTrigger>
                      <SelectContent>
                        {HEIGHT_OPTIONS.map((h) => <SelectItem key={h.cm} value={String(h.cm)}>{h.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  
                  <div>
                    <Label className="text-xs uppercase tracking-[0.12em] text-muted-foreground font-medium">Profile Photos</Label>
                    <p className="text-xs text-muted-foreground mb-4">Upload at least one photo (JPG/PNG/WEBP, max 8 MB).</p>
                    
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center mb-4">
                      <div className="flex-1">
                        <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => uploadFile(e.target.files?.[0])} className="text-sm w-full" data-testid="ob-photo-file" />
                        {uploading && <span className="text-xs text-muted-foreground">Uploading...</span>}
                      </div>
                      <div className="flex gap-2 items-center">
                        <span className="text-xs text-muted-foreground shrink-0">or URL:</span>
                        <Input value={photoUrl} onChange={(e) => setPhotoUrl(e.target.value)} placeholder="https://..." data-testid="ob-photo-url" className="min-w-[150px] h-9" />
                        <Button type="button" onClick={addPhoto} size="sm" className="rounded-full shrink-0"><Camera className="w-4 h-4 mr-1" />Add</Button>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-4 text-xs items-center">
                      <span className="text-muted-foreground">Quick sample photos:</span>
                      {samplePhotos(user?.gender).map((u) => (
                        <button key={u} type="button" className="text-secondary font-semibold hover:underline" onClick={() => update("photos", [...(data.photos || []), u])} data-testid="ob-sample-photo">Use Sample</button>
                      ))}
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
                      {(data.photos || []).map((u, i) => (
                        <div key={i} className="relative aspect-[3/4] rounded-xl overflow-hidden border border-border">
                          <img src={resolvePhotoUrl(u)} alt="Profile photo" className="w-full h-full object-cover" />
                          <button onClick={() => removePhoto(i)} className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1" data-testid={`ob-photo-rm-${i}`}><X className="w-3 h-3"/></button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {step === 1 && (
                <div className="space-y-4">
                  <Field label="District (Jharkhand)">
                    <Sel value={data.district} onChange={(v) => setData((d) => ({ ...d, district: v, block: "" }))} items={JHARKHAND_DISTRICTS} testid="ob-district" />
                  </Field>
                  <Field label="Block / Tehsil">
                    <Select value={data.block || ""} onValueChange={(v) => update("block", v)}>
                      <SelectTrigger data-testid="ob-block"><SelectValue placeholder="Select block" /></SelectTrigger>
                      <SelectContent>
                        {blocksFor(data.district).map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Village / Locality">
                    <Input value={data.village || ""} onChange={(e) => update("village", e.target.value)} placeholder="Your village name" data-testid="ob-village" />
                  </Field>
                  <Field label="City / Town">
                    <Input value={data.city || ""} onChange={(e) => update("city", e.target.value)} placeholder="Your city/town name" data-testid="ob-city" />
                  </Field>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-4">
                  <Field label="Religion">
                    <Sel value={data.religion} onChange={(v) => update("religion", v)} items={RELIGIONS} testid="ob-religion" />
                  </Field>
                  <Field label="Caste">
                    <SearchableSel
                      value={data.caste}
                      onChange={(v) => update("caste", v)}
                      items={JHARKHAND_CASTES}
                      placeholder="Select caste"
                      testid="ob-caste"
                    />
                  </Field>

                  {/* 🌸 BRAHMIN TRADITIONAL DETAILS SECTION IN MANUAL MODE ── */}
                  {isBrahmin(data.caste) && (
                    <div className="p-4 rounded-2xl bg-amber-500/10 border border-amber-500/30 space-y-3 animate-in fade-in duration-200">
                      <div className="flex items-center justify-between text-amber-900 font-bold text-xs">
                        <span className="flex items-center gap-1.5">
                          🌸 <span>Brahmin Traditional Details</span>
                        </span>
                        <span className="text-[10px] bg-amber-200/80 text-amber-900 font-semibold px-2 py-0.5 rounded-full">Gotra &amp; Mool / Dih</span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                        <div>
                          <Field label="Gotra">
                            <Input value={data.gotra || ""} onChange={(e) => update("gotra", e.target.value)} placeholder="e.g. Shandilya, Kashyap, Bharadwaj" className="bg-white text-xs h-9" />
                          </Field>
                        </div>
                        <div>
                          <Field label="Sub-Caste">
                            <Input value={data.subcaste || ""} onChange={(e) => update("subcaste", e.target.value)} placeholder="e.g. Maithil Brahmin, Kanyakubja" className="bg-white text-xs h-9" />
                          </Field>
                        </div>
                        <div>
                          <Field label="Mool / Dih (Origin Root)">
                            <Input value={data.mool || ""} onChange={(e) => update("mool", e.target.value)} placeholder="e.g. Rampuriye Rohar" className="bg-white text-xs h-9" />
                          </Field>
                        </div>
                        <div>
                          <Field label="Native Place / Village">
                            <Input value={data.native_place || ""} onChange={(e) => update("native_place", e.target.value)} placeholder="e.g. Andouli, Darbhanga" className="bg-white text-xs h-9" />
                          </Field>
                        </div>
                      </div>
                    </div>
                  )}

                  <Field label="Education">
                    <SearchableSel
                      value={data.education}
                      onChange={(v) => update("education", v)}
                      items={EDUCATION}
                      placeholder="Select highest education"
                      testid="ob-education"
                    />
                  </Field>
                  <Field label="Occupation / Job">
                    <SearchableSel
                      value={data.occupation}
                      onChange={(v) => update("occupation", v)}
                      items={OCCUPATIONS}
                      placeholder="Select or search occupation"
                      testid="ob-occupation"
                    />
                  </Field>
                  <Field label="Annual Income">
                    <Sel value={data.income || "No Income"} onChange={(v) => update("income", v)} items={["No Income", "Below 2 Lakhs", "2 - 5 Lakhs", "5 - 10 Lakhs", "10 - 20 Lakhs", "20+ Lakhs"]} testid="ob-income" />
                  </Field>
                  <Field label="Family Type">
                    <Sel value={data.family_type} onChange={(v) => update("family_type", v)} items={["Nuclear", "Joint"]} testid="ob-family" />
                  </Field>
                </div>
              )}

              <div className="mt-8 flex items-center justify-between pt-6 border-t border-border">
                {step > 0 ? (
                  <Button type="button" variant="ghost" onClick={prev} data-testid="ob-prev">Back</Button>
                ) : <div />}
                {step < STEPS.length - 1 ? (
                  <Button type="button" onClick={next} className="rounded-full px-6" data-testid="ob-next">Continue</Button>
                ) : (
                  <Button type="button" onClick={submit} disabled={saving} className="rounded-full px-6" data-testid="ob-submit">
                    {saving ? "Saving..." : "Complete Profile"}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.12em] text-muted-foreground font-medium mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}

function Sel({ value, onChange, items, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="bg-white"><SelectValue /></SelectTrigger>
      <SelectContent>
        {items.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function SearchableSel({ value, onChange, items, placeholder, testid }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = items.filter((i) =>
    i.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2 text-sm border rounded-md bg-white hover:bg-muted/50 text-left"
        data-testid={testid}
      >
        <span className={value ? "text-foreground font-medium" : "text-muted-foreground"}>
          {value || placeholder}
        </span>
        <ChevronDown className="w-4 h-4 opacity-50" />
      </button>

      {open && (
        <div className="absolute z-50 w-full mt-1 bg-popover text-popover-foreground border rounded-md shadow-lg max-h-60 overflow-hidden flex flex-col">
          <div className="p-2 border-b flex items-center gap-2">
            <Search className="w-4 h-4 opacity-50" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="w-full text-sm bg-transparent outline-none"
              autoFocus
            />
          </div>
          <div className="overflow-y-auto p-1 max-h-48">
            {filtered.length === 0 ? (
              <div className="p-2 text-xs text-muted-foreground text-center">No results found</div>
            ) : (
              filtered.map((item) => (
                <div
                  key={item}
                  onClick={() => {
                    onChange(item);
                    setOpen(false);
                    setSearch("");
                  }}
                  className={`flex items-center justify-between px-2 py-1.5 text-sm rounded cursor-pointer hover:bg-accent hover:text-accent-foreground ${
                    value === item ? "font-bold bg-accent/50" : ""
                  }`}
                >
                  <span>{item}</span>
                  {value === item && <Check className="w-4 h-4 text-primary" />}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
